import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';
import {
  createLibraryItem,
  createLibraryVersion,
  diffLibraryVersions,
  getLibraryItem,
  getLibraryVersion,
  listLibrary,
  listLibraryVersions,
  rollbackLibraryVersion,
  type TLibraryDiff,
  type TLibraryDiffLine,
  type TLibraryItem,
  type TLibraryVersion,
  type TLibraryVersionSummary,
} from '../library';

// Shared code is the Library's code items (automation §16): the same calls, narrowed to code.
export type TTransform = TLibraryItem;
export type TTransformCreate = { name: string; description?: string };
export type TTransformVersion = TLibraryVersion;
export type TTransformVersionSummary = TLibraryVersionSummary;
export type TCreatedVersion = TLibraryVersion;
export type TTransformUsage = components['schemas']['TransformUsageResDto'];
export type TVersionDiff = TLibraryDiff;
export type TDiffLine = TLibraryDiffLine;
export type TDryRun = components['schemas']['DryRunResDto'];
export type TDryRunInput = components['schemas']['DryRunInputDto'];
export type TDryRunConsoleLine = components['schemas']['DryRunConsoleLineDto'];
export type TBookmark = components['schemas']['BookmarkResDto'];

export const listTransforms = () => listLibrary('code');

export const createTransform = (body: TTransformCreate): Promise<TTransform> =>
  createLibraryItem({ kind: 'code', ...body });

export const getTransform = (id: string): Promise<TTransform> => getLibraryItem(id);

export const listVersions = (id: string): Promise<TTransformVersionSummary[]> =>
  listLibraryVersions(id);

export const getVersion = (id: string, version: number): Promise<TTransformVersion> =>
  getLibraryVersion(id, version);

export const diffVersions = (id: string, from: number, to: number): Promise<TVersionDiff> =>
  diffLibraryVersions(id, from, to);

export const createVersion = (
  id: string,
  code: string,
  comment?: string,
): Promise<TCreatedVersion> => createLibraryVersion(id, { code, ...(comment ? { comment } : {}) });

export const rollbackVersion = (
  id: string,
  version: number,
  comment: string,
): Promise<TCreatedVersion> => rollbackLibraryVersion(id, version, comment);

// There is no version parameter: without `code` the endpoint runs the latest version.
export const dryRun = (
  transformId: string,
  input: TDryRunInput,
  code?: string,
  env?: Record<string, string>,
): Promise<TDryRun> =>
  unwrap(
    api.POST('/v1/transforms/{transformId}/dry-run', {
      params: { path: { transformId } },
      body: { input, ...(code ? { code } : {}), ...(env ? { env } : {}) },
    }),
  );

export const listBookmarks = () =>
  unwrap(api.GET('/v1/bookmarks', { params: { query: { limit: 100 } } }));

export type TBookmarkPayload = components['schemas']['BookmarkPayloadResDto'];

export const bookmarkPayload = (name: string): Promise<TBookmarkPayload> =>
  unwrap(
    api.GET('/v1/bookmarks/by-name/{name}/payload', {
      params: { path: { name }, query: { body: 'original' } },
    }),
  );
